import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import DesignStudio, {
  type DesignStudioCatalog,
  type DesignStudioDraft,
  type DesignStudioResult,
} from '../components/design-studio/DesignStudio';
import EmailConsumerAuthModal from '../components/auth/EmailConsumerAuthModal';
import {
  createDirectDesignStudioRun,
  getDesignStudioCatalog,
  getDesignStudioRun,
  listDesignStudioRuns,
  resumeDesignStudioRun,
  uploadDesignStudioReferences,
  type DesignStudioRun,
} from '../lib/design-studio-api';
import { designStudioCanvasProgress } from '../lib/image-generation-api';
import { getCurrentWebConsumer } from '../lib/web-auth-api';
import { HttpJsonError } from '../lib/http-json';
import {
  getImageModelCatalog, IMAGE_MODEL_OPTIONS, imageModelParameterError,
  type ImageModelId, type ImageModelOption,
} from '../lib/image-model-catalog';
import { useWebConsumerAuthStore } from '../stores/useWebConsumerAuthStore';
import { useDesignStudioRunStore } from '../stores/useDesignStudioRunStore';

const WORKSPACE = 'meitu';

function displayError(error: unknown): string {
  return error instanceof Error ? error.message : '暂时无法完成操作，请稍后重试。';
}

function displayStatus(status: DesignStudioRun['status']): DesignStudioResult['status'] {
  return status === 'succeeded' ? 'completed' : status;
}

function linkedRunError(error: unknown): string {
  if (error instanceof HttpJsonError) {
    if (error.status === 401) return '登录状态已失效，请重新登录后查看任务。';
    if (error.status === 403) return '当前账号没有查看此创作任务的权限。';
    if (error.status === 404) return '任务不存在，或当前账号没有访问权限。';
    if (error.status === 422) return '任务链接无效，请从创作历史重新打开。';
  }
  return displayError(error);
}

function draftFingerprint(draft: DesignStudioDraft): string {
  return JSON.stringify({
    categoryId: draft.categoryId,
    skillId: draft.skillId,
    kind: draft.kind,
    prompt: draft.prompt,
    promptId: draft.promptId,
    aspectRatio: draft.aspectRatio,
    generationMode: draft.generationMode,
    imageModel: draft.imageModel,
    resolution: draft.resolution,
    quality: draft.quality,
    customDimensions: draft.customDimensions,
    outputWidth: draft.outputWidth,
    outputHeight: draft.outputHeight,
    transparentBackground: draft.transparentBackground,
    rightsConfirmed: draft.rightsConfirmed,
    nonOfficialTemplate: draft.nonOfficialTemplate,
    referenceFiles: draft.referenceFiles.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
    })),
  });
}

interface PendingSubmission {
  draft: DesignStudioDraft;
  fingerprint: string;
  requestId: string;
  resolve: (runId?: string) => void;
  reject: (reason: unknown) => void;
}

export default function GlobalDesignStudioRoutePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const routeKey = useRef(location.key);
  const mounted = useRef(true);
  routeKey.current = location.key;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const user = useWebConsumerAuthStore((state) => state.currentUser);
  const setCurrentUser = useWebConsumerAuthStore((state) => state.setCurrentUser);
  const clearCurrentUser = useWebConsumerAuthStore((state) => state.clearCurrentUser);
  const [authResolved, setAuthResolved] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [catalog, setCatalog] = useState<DesignStudioCatalog | null>(null);
  const [imageModels, setImageModels] = useState<ImageModelOption[]>(IMAGE_MODEL_OPTIONS);
  const [imageModelsError, setImageModelsError] = useState<string | null>(null);
  const runs = useDesignStudioRunStore((state) => state.runs);
  const setDesignStudioOwner = useDesignStudioRunStore((state) => state.setOwnerUser);
  const replaceDesignStudioRuns = useDesignStudioRunStore((state) => state.replaceRuns);
  const mergeDesignStudioRun = useDesignStudioRunStore((state) => state.mergeRun);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectedRunId = searchParams.get('run_id')?.toLowerCase() ?? null;
  const [linkedRunAccess, setLinkedRunAccess] = useState<{
    runId: string;
    ownerUserId: string | null;
    verified: boolean;
    error: string | null;
  } | null>(null);
  const referenceIds = useRef(new WeakMap<File, string>());
  const referenceOwnerId = useRef<string | null>(null);
  const referenceModelId = useRef<ImageModelId | null>(null);
  const requestIds = useRef(new Map<string, string>());
  const pendingSubmission = useRef<PendingSubmission | null>(null);

  useEffect(() => {
    setSubmitting(false);
    setError(null);
    setLoginOpen(false);
    pendingSubmission.current?.resolve(undefined);
    pendingSubmission.current = null;
    requestIds.current.clear();
  }, [location.key]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = '图片生成';
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    let ignored = false;
    void getCurrentWebConsumer().then((session) => {
      if (ignored) return;
      if (session.authenticated && session.user) setCurrentUser(session.user);
      else clearCurrentUser();
    }).catch(() => {
      if (!ignored) setError('登录状态确认失败，请刷新页面重试。');
    }).finally(() => {
      if (!ignored) setAuthResolved(true);
    });
    return () => { ignored = true; };
  }, [clearCurrentUser, setCurrentUser]);

  useEffect(() => {
    let ignored = false;
    setLoading(true);
    setError(null);
    const modelsPromise = getImageModelCatalog().catch((issue) => {
      if (!ignored) setImageModelsError(displayError(issue));
      return IMAGE_MODEL_OPTIONS;
    });
    void Promise.all([getDesignStudioCatalog(), modelsPromise]).then(([nextCatalog, nextModels]) => {
      if (ignored) return;
      setCatalog(nextCatalog);
      setImageModels(nextModels);
    }).catch((issue) => {
      if (!ignored) setError(displayError(issue));
    }).finally(() => {
      if (!ignored) setLoading(false);
    });
    return () => { ignored = true; };
  }, []);

  useEffect(() => {
    if (!authResolved) return;
    if (!user) {
      setDesignStudioOwner(null);
      referenceIds.current = new WeakMap();
      referenceOwnerId.current = null;
      referenceModelId.current = null;
      return;
    }
    setDesignStudioOwner(user.id);
    let ignored = false;
    void listDesignStudioRuns().then((nextRuns) => {
      if (!ignored) replaceDesignStudioRuns(user.id, nextRuns);
    }).catch((issue) => {
      if (!ignored) setError(displayError(issue));
    });
    return () => { ignored = true; };
  }, [authResolved, replaceDesignStudioRuns, setDesignStudioOwner, user?.id]);

  useEffect(() => {
    if (!selectedRunId || !authResolved) return;
    const ownerUserId = user?.id ?? null;
    setLinkedRunAccess({
      runId: selectedRunId, ownerUserId, verified: false,
      error: ownerUserId ? null : '请先登录，再查看和继续创作任务。',
    });
    if (!ownerUserId) return;
    let ignored = false;
    void getDesignStudioRun(selectedRunId).then((run) => {
      if (ignored) return;
      if (run.thread_id) {
        const params = new URLSearchParams({ workspace: 'houmao', thread_id: run.thread_id });
        if (run.agent_profile) params.set('agent_profile', run.agent_profile);
        navigate(`/chat?${params.toString()}`, { replace: true });
        return;
      }
      mergeDesignStudioRun(ownerUserId, run);
      setLinkedRunAccess({ runId: selectedRunId, ownerUserId, verified: true, error: null });
    }).catch((issue) => {
      if (!ignored) setLinkedRunAccess({
        runId: selectedRunId, ownerUserId, verified: false, error: linkedRunError(issue),
      });
    });
    return () => { ignored = true; };
  }, [authResolved, mergeDesignStudioRun, navigate, selectedRunId, user?.id]);

  useEffect(() => {
    if (!user || !runs.some((run) => run.status === 'queued' || run.status === 'running')) return;
    let ignored = false;
    const timer = window.setInterval(() => {
      const linkedRunPromise = selectedRunId && linkedRunAccess?.verified && linkedRunAccess.ownerUserId === user.id
        ? getDesignStudioRun(selectedRunId) : Promise.resolve(null);
      // Wait for both reads before updating the store: a faster history refresh
      // must not cancel the independent linked-run read during effect cleanup.
      void Promise.allSettled([listDesignStudioRuns(), linkedRunPromise]).then(([history, linked]) => {
        if (ignored) return;
        if (history.status === 'fulfilled') replaceDesignStudioRuns(user.id, history.value);
        if (linked.status === 'fulfilled' && linked.value) mergeDesignStudioRun(user.id, linked.value);
        else if (linked.status === 'rejected' && linked.reason instanceof HttpJsonError
          && [401, 403, 404].includes(linked.reason.status) && selectedRunId) {
          setLinkedRunAccess({
            runId: selectedRunId, ownerUserId: user.id, verified: false, error: linkedRunError(linked.reason),
          });
        }
        // Transient failures retain the last confirmed state for the next poll.
      });
    }, 3000);
    return () => {
      ignored = true;
      window.clearInterval(timer);
    };
  }, [linkedRunAccess, mergeDesignStudioRun, replaceDesignStudioRuns, runs, selectedRunId, user?.id]);

  const submitAuthenticated = useCallback(async (
    draft: DesignStudioDraft,
    fingerprint: string,
    requestId: string,
  ) => {
    const submissionRouteKey = routeKey.current;
    const isCurrentRoute = () => mounted.current && routeKey.current === submissionRouteKey;
    setSubmitting(true);
    setError(null);
    const missing: File[] = [];
    try {
      const currentUser = useWebConsumerAuthStore.getState().currentUser;
      if (!currentUser) throw new Error('请先登录，再使用图片生成功能。');
      if (draft.kind !== 'text') {
        const model = imageModels.find((option) => option.id === draft.imageModel);
        if (!model) throw new Error('图片模型尚未加载，请刷新页面重试。');
        const parameterError = imageModelParameterError(model, {
          aspectRatio: draft.aspectRatio, resolution: draft.resolution, quality: draft.quality,
          outputWidth: draft.outputWidth, outputHeight: draft.outputHeight,
          referenceCount: draft.referenceFiles.length,
        });
        if (parameterError) throw new Error(parameterError);
      }
      if (referenceOwnerId.current !== currentUser.id || referenceModelId.current !== (draft.imageModel ?? null)) {
        referenceIds.current = new WeakMap();
        referenceOwnerId.current = currentUser.id;
        referenceModelId.current = draft.imageModel ?? null;
      }
      for (const file of draft.referenceFiles) {
        const id = referenceIds.current.get(file);
        if (!id) missing.push(file);
      }
      if (missing.length) {
        const uploaded = await uploadDesignStudioReferences(missing, draft.imageModel);
        uploaded.forEach((id, index) => referenceIds.current.set(missing[index], id));
      }
      const orderedIds = draft.referenceFiles.map((file) => referenceIds.current.get(file) ?? '');
      const conversationId = runs.find((run) => run.run_id === selectedRunId)?.conversation_id || selectedRunId;
      const run = await createDirectDesignStudioRun(draft, orderedIds, requestId, conversationId);
      mergeDesignStudioRun(currentUser.id, run);
      requestIds.current.delete(fingerprint);
      if (!isCurrentRoute()) return;
      if (!selectedRunId) {
        const next = new URLSearchParams(searchParams);
        next.set('run_id', run.conversation_id || run.run_id);
        setSearchParams(next, { replace: true });
      }
      return run.run_id;
    } catch (issue) {
      if (isCurrentRoute()) setError(displayError(issue));
      throw issue;
    } finally {
      if (isCurrentRoute()) setSubmitting(false);
    }
  }, [imageModels, mergeDesignStudioRun, runs, selectedRunId, searchParams, setSearchParams]);

  const submit = useCallback((draft: DesignStudioDraft): Promise<string | void> => {
    const fingerprint = `${selectedRunId ?? 'new'}:${draftFingerprint(draft)}`;
    const requestId = requestIds.current.get(fingerprint) ?? crypto.randomUUID();
    requestIds.current.set(fingerprint, requestId);
    if (useWebConsumerAuthStore.getState().currentUser) {
      return submitAuthenticated(draft, fingerprint, requestId);
    }
    setLoginOpen(true);
    return new Promise<string | void>((resolve, reject) => {
      pendingSubmission.current = { draft, fingerprint, requestId, resolve, reject };
    });
  }, [selectedRunId, submitAuthenticated]);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    const pending = pendingSubmission.current;
    pendingSubmission.current = null;
    pending?.resolve(undefined);
  }, []);

  const finishLogin = useCallback((current: Parameters<typeof setCurrentUser>[0]) => {
    setCurrentUser(current);
    setLoginOpen(false);
    const pending = pendingSubmission.current;
    pendingSubmission.current = null;
    if (!pending) return;
    void submitAuthenticated(pending.draft, pending.fingerprint, pending.requestId)
      .then(pending.resolve, pending.reject);
  }, [setCurrentUser, submitAuthenticated]);

  useEffect(() => {
    if (!authResolved || !user) return;
    const pending = pendingSubmission.current;
    if (!pending) return;
    pendingSubmission.current = null;
    setLoginOpen(false);
    void submitAuthenticated(pending.draft, pending.fingerprint, pending.requestId)
      .then(pending.resolve, pending.reject);
  }, [authResolved, submitAuthenticated, user]);

  const retry = useCallback((runId: string) => {
    setError(null);
    void resumeDesignStudioRun(runId).then((run) => {
      const currentUser = useWebConsumerAuthStore.getState().currentUser;
      if (currentUser) mergeDesignStudioRun(currentUser.id, run);
    }).catch((issue) => setError(displayError(issue)));
  }, [mergeDesignStudioRun]);

  const results = useMemo<DesignStudioResult[]>(() => {
    return runs.map((run) => ({
      id: run.run_id,
      title: catalog?.categories.flatMap((category) => category.skills)
        .find((skill) => skill.id === run.skill_id)?.title ?? '创作任务',
      kind: run.kind,
      status: displayStatus(run.status),
      canvasProgress: designStudioCanvasProgress(run),
      previewUrls: run.items.flatMap((item) => item.status === 'succeeded' && item.artifact?.url
        && (!item.canvas_progress || item.canvas_progress.status === 'succeeded') ? [item.artifact.url] : []),
      images: run.items.flatMap((item) => {
        const artifact = item.artifact;
        if (item.status !== 'succeeded' || !artifact?.url
          || (item.canvas_progress && item.canvas_progress.status !== 'succeeded')) return [];
        return [{
          imageId: typeof artifact.image_id === 'string' ? artifact.image_id : undefined,
          url: artifact.url,
          downloadUrl: typeof artifact.download_url === 'string' ? artifact.download_url : artifact.url,
          width: typeof artifact.width === 'number' ? artifact.width : undefined,
          height: typeof artifact.height === 'number' ? artifact.height : undefined,
          model: typeof artifact.model === 'string' ? artifact.model : undefined,
          originalImageId: typeof artifact.original_image_id === 'string' ? artifact.original_image_id : undefined,
          originalKind: artifact.original_kind === 'generation' ? 'generation' as const : undefined,
          originalDownloadUrl: typeof artifact.original_download_url === 'string' ? artifact.original_download_url : undefined,
          originalWidth: typeof artifact.original_width === 'number' ? artifact.original_width : undefined,
          originalHeight: typeof artifact.original_height === 'number' ? artifact.original_height : undefined,
        }];
      }),
      downloadUrl: run.download_url ?? undefined,
      text: run.items.flatMap((item) => item.text ? [item.text] : []).join('\n\n'),
      error: run.status === 'failed'
        ? run.error_message || designStudioCanvasProgress(run)?.error || '部分内容尚未完成，可继续生成。' : undefined,
      settledCreditMicros: run.items.reduce(
        (total, item) => total + Math.max(0, item.settled_credit_micros ?? 0),
        0,
      ),
    }));
  }, [catalog, runs]);

  const currentLinkedRunAccess = linkedRunAccess?.runId === selectedRunId
    && linkedRunAccess.ownerUserId === (user?.id ?? null) ? linkedRunAccess : null;
  const restoredRun = selectedRunId && currentLinkedRunAccess?.verified
    ? runs.find((run) => run.run_id === selectedRunId) ?? null
    : null;
  const restoredTurns = useMemo(() => restoredRun ? runs
    .filter((run) => (run.conversation_id || run.run_id) === (restoredRun.conversation_id || restoredRun.run_id))
    .sort((left, right) => Date.parse(left.created_at ?? '') - Date.parse(right.created_at ?? ''))
    .map((run) => ({ id: run.run_id, prompt: run.prompt, resultId: run.run_id })) : undefined,
  [restoredRun, runs]);

  if (loading || !catalog) {
    return <div className="design-studio-gate flex min-h-full w-full items-center justify-center overflow-y-auto px-5 py-12" role={error ? 'alert' : 'status'}>
      <div className="w-full max-w-lg rounded-3xl border border-emerald-900/10 bg-white p-8 shadow-lg shadow-emerald-950/5 sm:p-12">
        <h1 className="text-2xl font-bold text-[#16362e]">{error ? '创作工具暂时无法加载' : '正在载入创作工具'}</h1>
        <p className="mt-3 text-sm leading-7 text-stone-600">{error ?? '正在载入技能与历史作品…'}</p>
        {error && <button className="mt-7 min-h-11 rounded-xl bg-emerald-700 px-6 py-2 font-semibold text-white hover:bg-emerald-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700" type="button" onClick={() => window.location.reload()}>重新加载</button>}
      </div>
    </div>;
  }

  return <>
    {selectedRunId && !restoredRun ? <section className="design-studio-gate flex min-h-full w-full items-center justify-center overflow-y-auto px-5 py-12"
      role={currentLinkedRunAccess?.error ? 'alert' : 'status'}>
      <div className="w-full max-w-lg rounded-3xl border border-emerald-900/10 bg-white p-8 sm:p-12">
        <h1 className="text-2xl font-bold">{currentLinkedRunAccess?.error ? '暂时无法打开创作任务' : '正在加载创作任务'}</h1>
        <p className="mt-3 text-sm leading-7">{currentLinkedRunAccess?.error ?? '正在核实任务归属并载入已完成的作品…'}</p>
        {authResolved && !user && <button type="button" className="mt-5 rounded-xl px-5 py-3" onClick={() => setLoginOpen(true)}>登录后查看任务</button>}
        {currentLinkedRunAccess?.error && user && <button type="button" className="mt-5 rounded-xl px-5 py-3" onClick={() => window.location.reload()}>重新加载</button>}
        <a className="ml-4 inline-block text-sm underline" href="/design-studio">开始新的创作</a>
      </div>
    </section> : <DesignStudio
      key={selectedRunId ?? 'new'}
      catalog={catalog}
      imageModels={imageModels}
      imageModelsError={imageModelsError}
      onSubmit={submit}
      isSubmitting={submitting}
      submitError={error}
      results={results}
      restoredTurns={restoredTurns}
      restoredRun={restoredRun ? {
        id: restoredRun.run_id,
        prompt: restoredRun.prompt,
        imageModel: restoredRun.image_model,
        resolution: restoredRun.resolution,
        quality: restoredRun.quality,
        aspectRatio: restoredRun.aspect_ratio,
        generationMode: restoredRun.generation_mode,
        customDimensions: restoredRun.custom_dimensions ?? undefined,
        outputWidth: restoredRun.output_width,
        outputHeight: restoredRun.output_height,
        transparentBackground: restoredRun.transparent_background,
      } : null}
      onRetry={retry}
    />}
    <EmailConsumerAuthModal
      isOpen={loginOpen}
      workspace={WORKSPACE}
      guestConversations={[]}
      onClose={closeLogin}
      onAuthenticated={finishLogin}
    />
  </>;
}
